<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import Button from 'dashboard/components-next/button/Button.vue';
import AiAgentForm from './AiAgentForm.vue';

const store = useStore();

const agents = computed(() => store.getters['aiAgents/getAgents']);
const uiFlags = computed(() => store.getters['aiAgents/getUIFlags']);

const showForm = ref(false);
const editingAgent = ref(null);

onMounted(() => {
  store.dispatch('aiAgents/fetchAgents');
});

const openCreateForm = () => {
  editingAgent.value = null;
  showForm.value = true;
};

const openEditForm = async agent => {
  const fullAgent = await store.dispatch('aiAgents/fetchAgent', agent.id);
  editingAgent.value = fullAgent;
  showForm.value = true;
};

const closeForm = () => {
  showForm.value = false;
  editingAgent.value = null;
};

const onSave = async formData => {
  if (editingAgent.value) {
    await store.dispatch('aiAgents/updateAgent', {
      agentId: editingAgent.value.id,
      agentData: formData,
    });
  } else {
    await store.dispatch('aiAgents/createAgent', formData);
  }
  closeForm();
};

const onDelete = async agentId => {
  await store.dispatch('aiAgents/deleteAgent', agentId);
};
</script>

<template>
  <div class="flex w-full flex-col gap-4">
    <div class="flex items-center justify-between">
      <div>
        <h2 class="text-lg font-semibold text-n-slate-12">Agentes IA</h2>
        <p class="mt-0.5 text-sm text-n-slate-10">
          Configure agentes IA com webhook para chat em tempo real.
        </p>
      </div>
      <Button
        size="sm"
        variant="solid"
        color-scheme="primary"
        icon="i-lucide-plus"
        label="Novo Agente"
        @click="openCreateForm"
      />
    </div>

    <div
      v-if="uiFlags.isFetchingAgents"
      class="flex items-center justify-center py-12"
    >
      <span class="i-lucide-loader-2 animate-spin text-2xl text-n-slate-10" />
    </div>

    <div
      v-else-if="agents.length === 0"
      class="flex flex-col items-center gap-3 rounded-xl border border-dashed border-n-weak py-12"
    >
      <div
        class="flex size-12 items-center justify-center rounded-2xl bg-n-alpha-1"
      >
        <span class="i-lucide-bot-message-square text-xl text-n-slate-10" />
      </div>
      <p class="text-sm text-n-slate-10">Nenhum agente IA configurado.</p>
      <Button
        size="sm"
        variant="ghost"
        color-scheme="primary"
        label="Criar primeiro agente"
        @click="openCreateForm"
      />
    </div>

    <div v-else class="overflow-hidden rounded-xl border border-n-weak">
      <table class="w-full text-sm">
        <thead>
          <tr class="border-b border-n-weak bg-n-alpha-1 text-left">
            <th class="px-4 py-2.5 font-medium text-n-slate-11">Nome</th>
            <th class="px-4 py-2.5 font-medium text-n-slate-11">
              Webhook URL
            </th>
            <th class="px-4 py-2.5 font-medium text-n-slate-11">Status</th>
            <th class="px-4 py-2.5 font-medium text-n-slate-11">Ações</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="agent in agents"
            :key="agent.id"
            class="border-b border-n-weak last:border-0"
          >
            <td class="px-4 py-3">
              <div class="flex items-center gap-2">
                <div
                  class="flex size-8 items-center justify-center rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 text-xs font-bold text-white"
                >
                  {{
                    agent.name
                      .split(' ')
                      .map(w => w[0])
                      .join('')
                      .slice(0, 2)
                      .toUpperCase()
                  }}
                </div>
                <div>
                  <span class="font-medium text-n-slate-12">
                    {{ agent.name }}
                  </span>
                  <p
                    v-if="agent.description"
                    class="truncate text-xs text-n-slate-10"
                    style="max-width: 200px"
                  >
                    {{ agent.description }}
                  </p>
                </div>
              </div>
            </td>
            <td class="px-4 py-3">
              <code
                class="rounded bg-n-alpha-1 px-1.5 py-0.5 text-xs text-n-slate-11"
              >
                {{ agent.webhook_url }}
              </code>
            </td>
            <td class="px-4 py-3">
              <span
                class="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium"
                :class="
                  agent.active
                    ? 'bg-emerald-500/10 text-emerald-600'
                    : 'bg-n-alpha-1 text-n-slate-10'
                "
              >
                <span
                  class="size-1.5 rounded-full"
                  :class="agent.active ? 'bg-emerald-500' : 'bg-n-slate-8'"
                />
                {{ agent.active ? 'Ativo' : 'Inativo' }}
              </span>
            </td>
            <td class="px-4 py-3">
              <div class="flex items-center gap-1">
                <Button
                  size="xs"
                  variant="ghost"
                  color-scheme="secondary"
                  icon="i-lucide-pencil"
                  @click="openEditForm(agent)"
                />
                <Button
                  size="xs"
                  variant="ghost"
                  color-scheme="alert"
                  icon="i-lucide-trash-2"
                  @click="onDelete(agent.id)"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <AiAgentForm
      v-if="showForm"
      :agent="editingAgent"
      @save="onSave"
      @close="closeForm"
    />
  </div>
</template>
